import { usePackageBookingStore } from "@elixir/stores";
import {
  APPOINTMENT_STATUS,
  Appointment,
  CompletedQueueStatus,
  OpdQueueStatus,
  OtherQueueStatus,
  PACKAGE_POPUP_TYPE,
  PackageDetailsByPatientIdInclusion,
  QUEUE_TYPE,
} from "@elixir/types";
import { getPatientMainInfoString } from "@elixir/utils";
import React, { useMemo } from "react";
import {
  CurrentTreatmentHeaderComponentUi,
  CurrentTreatmentPackageInfoComponentUi,
  SessionInfoComponentUi,
  Spinner,
} from "ui";
import useGetPackageDetailsByTransactionId from "../../../../apis/tryst/packages-and-treatments/use-get-package-details-by-transaction-id";
import usePostRedeemPacakge from "../../../../apis/tryst/packages-and-treatments/use-post-redeem-package";
import useGetPatientOverview from "../../../../apis/tryst/use-get-patient-overview";
import useQueueStore from "../../../../stores/queue-store";
import { navigateLocal } from "../../../queue/queue-header";

const CurrentTreatment = ({
  showRedeemPackage = true,
  showBookSlot = true,
  onStartClick,
}: {
  showRedeemPackage?: boolean;
  showBookSlot?: boolean;
  onStartClick?: () => void;
}) => {
  const transactionId = usePackageBookingStore((state) => state.transactionId);
  const setTransactionId = usePackageBookingStore(
    (state) => state.setTransactionId
  );
  const selectedAppointment = usePackageBookingStore(
    (state) => state.selectedAppointment
  );
  const selectedPatient = usePackageBookingStore(
    (state) => state.selectedPatient
  );

  const patient = selectedPatient || selectedAppointment?.patient;

  const { data: patientOverviewData, isLoading } = useGetPatientOverview({
    patientId: patient?.oid!,
  });

  const transactionIdToFetch =
    transactionId ||
    selectedAppointment?.package_inclusions?.[0]?.package_transaction
      ?.package_transaction_id ||
    patientOverviewData?.package?.package_transaction_id;

  const setAptSelectedPatient = useQueueStore(
    (state) => state.setSelectedPatient
  );

  const setOpenLeftPanel = useQueueStore((state) => state.setOpenLeftPanel);
  const setLeftPanelQueueType = useQueueStore(
    (state) => state.setLeftPanelQueueType
  );

  const setPackagePopupType = usePackageBookingStore(
    (state) => state.setPackagePopupType
  );
  const selectedPackagePatient = usePackageBookingStore(
    (state) => state.selectedPatient
  );

  const title = useMemo(
    () =>
      getPatientMainInfoString({
        patient: {
          dob: patient?.dob || "",
          fln: patient?.fln || "",
          gen: patient?.gen || "",
        },
      }).join(" | "),
    [patient]
  );

  const { data: packageData, isLoading: isPackageDetailsLoading } =
    useGetPackageDetailsByTransactionId({
      transctionId: transactionIdToFetch!,
      inclusionId: selectedAppointment?.inclusion_ids?.[0],
    });

  const { mutateAsync: redeemPackage } = usePostRedeemPacakge();

  const handleRedeemPackageClick = async (
    inclusion: PackageDetailsByPatientIdInclusion
  ) => {
    if (selectedAppointment?.aid && inclusion) {
      await redeemPackage({
        aid: selectedAppointment?.aid,
        inclusion_ids: [inclusion.inclusion_id],
      });
    }
  };

  const handleBookSlotClick = () => {
    const patient = selectedAppointment?.patient || selectedPackagePatient;
    if (!patient) return;
    setLeftPanelQueueType("add-new");
    setOpenLeftPanel(true);

    setAptSelectedPatient(patient);
    setPackagePopupType(PACKAGE_POPUP_TYPE.NONE);
  };

  const setSelectedDate = useQueueStore((state) => state.setSelectedDate);
  const setScrollToValues = useQueueStore((state) => state.setScrollToValues);
  const setClinicId = useQueueStore((state) => state.setClinicId);
  const setRightPanelQueueType = useQueueStore(
    (state) => state.setRightPanelQueueType
  );

  const handleGoToAptClick = (apt?: Appointment) => {
    if (!apt) return;
    setPackagePopupType(PACKAGE_POPUP_TYPE.NONE);
    const { date, status, aid, clinic_id } = apt;
    setSelectedDate(new Date(date));
    if (status === APPOINTMENT_STATUS.BOOKED) {
      setOpenLeftPanel(true);
      setLeftPanelQueueType(QUEUE_TYPE.BOOKED);
      setScrollToValues({ aid: aid, queueType: QUEUE_TYPE.BOOKED });
      navigateLocal(apt.clinic_id, QUEUE_TYPE.BOOKED);
    } else if (OpdQueueStatus.includes(status)) {
      setRightPanelQueueType(QUEUE_TYPE.CHECKED_IN);
      setScrollToValues({ aid: aid, queueType: QUEUE_TYPE.CHECKED_IN });
      navigateLocal(apt.clinic_id, undefined, QUEUE_TYPE.CHECKED_IN);
    } else if (CompletedQueueStatus.includes(status)) {
      setRightPanelQueueType(QUEUE_TYPE.COMPLETED);
      setScrollToValues({ aid: aid, queueType: QUEUE_TYPE.COMPLETED });
      navigateLocal(apt.clinic_id, undefined, QUEUE_TYPE.COMPLETED);
    } else if (OtherQueueStatus.includes(status)) {
      setOpenLeftPanel(true);
      setLeftPanelQueueType(QUEUE_TYPE.OTHERS);
      setScrollToValues({ aid: aid, queueType: QUEUE_TYPE.OTHERS });
      navigateLocal(apt.clinic_id, QUEUE_TYPE.OTHERS);
    } else {
      setOpenLeftPanel(true);
      setLeftPanelQueueType(QUEUE_TYPE.FOLLOWUP);
      setScrollToValues({ aid: aid, queueType: QUEUE_TYPE.FOLLOWUP });
      navigateLocal(apt.clinic_id, QUEUE_TYPE.FOLLOWUP);
    }

    setClinicId(clinic_id);
  };

  React.useEffect(() => {
    return () => {
      setTransactionId();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rightPanelQueueType = useQueueStore(
    (state) => state.rightPanelQueueType
  );
  const leftPanelQueueType = useQueueStore((state) => state.leftPanelQueueType);

  const isRedeemPackage =
    showRedeemPackage &&
    (rightPanelQueueType === QUEUE_TYPE.CHECKED_IN ||
      leftPanelQueueType === QUEUE_TYPE.BOOKED);

  return (
    <div className="dr-relative dr-h-full dr-w-full dr-flex dr-flex-col dr-overflow-hidden">
      <CurrentTreatmentHeaderComponentUi
        title={title}
        onStartClick={onStartClick}
      />

      {isPackageDetailsLoading ? (
        <div className="dr-w-full dr-h-full dr-flex dr-justify-center dr-items-center">
          <Spinner size={24} className="dr-text-darwin-primary-primary" />
        </div>
      ) : (
        <>
          <CurrentTreatmentPackageInfoComponentUi packageData={packageData} />
          <SessionInfoComponentUi
            handleGoToAppointment={handleGoToAptClick}
            handleRedeemPackageClick={handleRedeemPackageClick}
            sessions={packageData?.package_inclusion || []}
            onBookSlotClick={handleBookSlotClick}
            isRedeemPackage={isRedeemPackage}
            showBookSlot={showBookSlot}
          />
        </>
      )}
    </div>
  );
};

export default CurrentTreatment;
